'use client'

// User profile and KYC verification component
// Handles personal information and document uploads for landlord verification
import { useState } from 'react'
import VerificationBadge from './VerificationBadge'
import { User } from '@/types'
import { Upload, CheckCircle2, XCircle, Clock, AlertCircle, FileText, User as UserIcon, MapPin, Calendar, Phone, Building2, CreditCard } from 'lucide-react'

interface UserProfileProps {
  user: User
  onUpdate?: () => void
  onCancel?: () => void
}

export default function UserProfile({ user, onUpdate, onCancel }: UserProfileProps) {
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadingDocType, setUploadingDocType] = useState<string | null>(null)

  // Check verification status - prioritize kyc.status, then kyc_data.verification_status
  const isVerified = user?.is_verified === true
  
  // Get KYC status from backend - check both kyc.status and kyc_data.verification_status
  // null/undefined means user has NOT applied for KYC yet
  const kycStatus = user?.kyc?.status || user?.kyc_data?.verification_status || null
  
  // Check if documents have been uploaded (but not yet submitted for review)
  const hasUploadedDocuments = (user?.kyc?.documents && user.kyc.documents.length > 0) || 
                                (user?.kyc_data?.documents?.idDocument && user?.kyc_data?.documents?.proofOfAddress)
  
  // Check if user has applied for KYC (status is set, meaning documents were submitted)
  const hasAppliedForKyc = kycStatus !== null && kycStatus !== undefined
  
  // Pending ONLY means: backend status is explicitly 'pending' or 'in_review'
  // This means user HAS applied and it's under review
  const hasPendingKyc = kycStatus === 'pending' || kycStatus === 'in_review'
  
  // Rejected means: status is explicitly 'rejected'
  const isRejected = kycStatus === 'rejected'
  
  // KYC Needed means: user has not applied for KYC yet (status is null/undefined)
  const needsKyc = !hasAppliedForKyc
  
  // Lock form if verified or if KYC is pending/in_review (but allow editing if rejected)
  const isLocked = isVerified || (hasPendingKyc && !isRejected)

  // Helper function to format date for input field
  const formatDateForInput = (date: string | Date | undefined): string => {
    if (!date) return ''
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date
      if (isNaN(dateObj.getTime())) return ''
      const year = dateObj.getFullYear()
      const month = String(dateObj.getMonth() + 1).padStart(2, '0')
      const day = String(dateObj.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    } catch {
      return ''
    }
  }

  // Payment account state (for landlords) - used for rental payouts after commission
  const [paymentAccount, setPaymentAccount] = useState({
    accountType: (user as any)?.paymentAccount?.accountType || 'bank',
    bankName: (user as any)?.paymentAccount?.bankName || '',
    accountName: (user as any)?.paymentAccount?.accountName || '',
    accountNumber: (user as any)?.paymentAccount?.accountNumber || '',
    routingNumber: (user as any)?.paymentAccount?.routingNumber || '',
    swiftCode: (user as any)?.paymentAccount?.swiftCode || '',
    iban: (user as any)?.paymentAccount?.iban || '',
    mobileMoneyProvider: (user as any)?.paymentAccount?.mobileMoneyProvider || '',
    mobileMoneyNumber: (user as any)?.paymentAccount?.mobileMoneyNumber || '',
    country: (user as any)?.paymentAccount?.country || '',
    currency: (user as any)?.paymentAccount?.currency || 'NGN'
  })
  const [savingBankAccount, setSavingBankAccount] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    firstName: user?.firstName || user?.kyc_data?.firstName || '',
    lastName: user?.lastName || user?.kyc_data?.lastName || '',
    phone: user?.phone || user?.kyc_data?.phone || '',
    address: user?.profile?.address?.street || user?.kyc_data?.address || '',
    dateOfBirth: formatDateForInput(user?.profile?.dateOfBirth || user?.kyc_data?.dateOfBirth)
  })

  // KYC documents state
  const [kycDocuments, setKycDocuments] = useState({
    idDocument: user.kyc_data?.documents?.idDocument || '',
    proofOfAddress: user.kyc_data?.documents?.proofOfAddress || '',
    bankStatement: user.kyc_data?.documents?.bankStatement || '',
    employmentLetter: user.kyc_data?.documents?.employmentLetter || ''
  })

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  // Handle payment account (bank) field changes for landlords
  const handlePaymentAccountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setPaymentAccount(prev => ({
      ...prev,
      [name]: value
    }))
  }

  // Save only bank account details (for landlords - e.g. after verification or without resubmitting KYC)
  const handleSaveBankAccount = async () => {
    if ((user as any)?.role !== 'landlord') return
    if (!paymentAccount.bankName?.trim() || !paymentAccount.accountName?.trim() || !paymentAccount.accountNumber?.trim()) {
      setMessage('Please fill in Bank Name, Account Name, and Account Number.')
      return
    }
    setSavingBankAccount(true)
    setMessage('')
    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        setMessage('Please log in to save bank details.')
        setSavingBankAccount(false)
        return
      }
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
      const payload = {
        ...paymentAccount,
        accountType: paymentAccount.accountType || 'bank'
      }
      const res = await fetch(`${apiUrl}/users/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ paymentAccount: payload })
      })
      const data = await res.json()
      if (!res.ok) {
        setMessage(data.message || 'Failed to save bank account details.')
        setSavingBankAccount(false)
        return
      }
      setMessage('Bank account details saved successfully. We will use this account for your rental payouts after commission.')
      if (onUpdate) onUpdate()
    } catch (err) {
      console.error('Save bank account error:', err)
      setMessage('Failed to save bank account details. Please try again.')
    } finally {
      setSavingBankAccount(false)
    }
  }

  // Handle KYC document upload
  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>, documentType: string) => {
    // Prevent upload if KYC is pending or verified
    if (isLocked) {
      if (isVerified) {
        setMessage('Profile is locked after verification. Contact support to request changes.')
      } else if (hasPendingKyc) {
        setMessage('Your profile is pending admin review. You cannot upload documents until the review is complete.')
      }
      e.target.value = '' // Clear the file input
      return
    }

    const files = e.target.files
    if (!files || files.length === 0) return

    setUploading(true)
    setUploadingDocType(documentType)
    setMessage('')

    try {
      const file = files[0]
      
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']
      if (!allowedTypes.includes(file.type)) {
        setMessage('Please upload PDF, JPG, or PNG files only.')
        setUploading(false)
        setUploadingDocType(null)
        return
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        setMessage('File size must be less than 10MB.')
        setUploading(false)
        setUploadingDocType(null)
        return
      }

      // Get auth token
      const token = localStorage.getItem('authToken')
      if (!token) {
        setMessage('Please log in to upload documents.')
        setUploading(false)
        setUploadingDocType(null)
        return
      }

      // Upload file to backend
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
      const formData = new FormData()
      formData.append('file', file)

      const response = await fetch(`${apiUrl}/upload/single`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      })

      const data = await response.json()

      if (!response.ok) {
        setMessage(data.message || 'Failed to upload document. Please try again.')
        setUploading(false)
        setUploadingDocType(null)
        return
      }

      // Update KYC documents state
      setKycDocuments(prev => ({
        ...prev,
        [documentType]: data.file.url
      }))

      setMessage('Document uploaded successfully!')
    } catch (error) {
      console.error('Upload error:', error)
      setMessage('Failed to upload document. Please try again.')
    } finally {
      setUploading(false)
      setUploadingDocType(null)
    }
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage('')
    
    if (isLocked) {
      if (isVerified) {
        setMessage('Profile is locked after verification. Contact support to request changes.')
      } else if (hasPendingKyc) {
        setMessage('Your profile is pending admin review. You cannot make changes until the review is complete.')
      }
      return
    }

    // Validate required documents
    if (!kycDocuments.idDocument || !kycDocuments.proofOfAddress) {
      setMessage('Please upload both required documents (Government ID and Proof of Address) before submitting.')
      return
    }

    setLoading(true)

    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        setMessage('Please log in to update your profile.')
        setLoading(false)
        return
      }

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
      console.log('Submitting KYC verification with API URL:', apiUrl)

      // Step 1: Update user profile
      console.log('Step 1: Updating user profile...')
      const profileResponse = await fetch(`${apiUrl}/users/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          profile: {
            dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth).toISOString() : undefined,
            address: {
              street: formData.address
            }
          }
        })
      })

      if (!profileResponse.ok) {
        let errorMessage = 'Failed to update profile. Please try again.'
        try {
          const errorData = await profileResponse.json()
          errorMessage = errorData.message || errorMessage
        } catch (parseError) {
          // If response is not JSON, use status text
          errorMessage = `Server error (${profileResponse.status}): ${profileResponse.statusText}`
        }
        setMessage(errorMessage)
        setLoading(false)
        return
      }

      // Step 2: Submit KYC documents
      console.log('Step 2: Submitting KYC documents...')
      const documentsToSubmit = []
      
      if (kycDocuments.idDocument) {
        documentsToSubmit.push({
          type: 'id',
          url: kycDocuments.idDocument
        })
      }
      
      if (kycDocuments.proofOfAddress) {
        documentsToSubmit.push({
          type: 'proof_of_address',
          url: kycDocuments.proofOfAddress
        })
      }

      if (kycDocuments.bankStatement) {
        documentsToSubmit.push({
          type: 'income_proof',
          url: kycDocuments.bankStatement
        })
      }

      if (kycDocuments.employmentLetter) {
        documentsToSubmit.push({
          type: 'income_proof',
          url: kycDocuments.employmentLetter
        })
      }

      if (documentsToSubmit.length > 0) {
        const kycResponse = await fetch(`${apiUrl}/users/upload-kyc`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            documents: documentsToSubmit
          })
        })

        if (!kycResponse.ok) {
          let errorMessage = 'Profile updated but failed to submit documents. Please try uploading documents again.'
          try {
            const errorData = await kycResponse.json()
            errorMessage = errorData.message || errorMessage
          } catch (parseError) {
            // If response is not JSON, use status text
            errorMessage = `Server error (${kycResponse.status}): ${kycResponse.statusText}. Please try uploading documents again.`
          }
          setMessage(errorMessage)
          setLoading(false)
          return
        }
      }

      // Update payment account details (bank account for rental payouts after commission)
      const hasBankDetails = (user as any)?.role === 'landlord' &&
        (paymentAccount.bankName?.trim() || paymentAccount.accountName?.trim() || paymentAccount.accountNumber?.trim())
      if (hasBankDetails) {
        const payload = { ...paymentAccount, accountType: paymentAccount.accountType || 'bank' }
        const paymentAccountResponse = await fetch(`${apiUrl}/users/profile`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ paymentAccount: payload })
        })
        if (!paymentAccountResponse.ok) {
          console.warn('Failed to update payment account, but profile and documents were saved.')
        }
      }

      setMessage('Profile and documents submitted successfully! Your verification request has been sent to admin for review.')
      
      if (onUpdate) {
        onUpdate()
      } else {
        window.location.reload()
      }
    } catch (error) {
      console.error('Profile update error:', error)
      
      // Provide more specific error messages
      if (error instanceof TypeError && error.message.includes('fetch')) {
        setMessage('Network error: Unable to connect to server. Please check your internet connection and try again.')
      } else if (error instanceof Error) {
        setMessage(`Error: ${error.message}`)
      } else {
        setMessage('An error occurred. Please check the console for details and try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  // Get verification status info
  const getStatusInfo = () => {
    // First check if user is verified
    if (isVerified) {
      return {
        icon: CheckCircle2,
        title: 'Verified',
        description: 'Your account has been verified by our admin team. You can now list properties and access all platform features.',
        color: 'emerald',
        bgColor: 'bg-emerald-50',
        borderColor: 'border-emerald-200',
        textColor: 'text-emerald-700',
        titleColor: 'text-emerald-900'
      }
    }
    
    // Check if explicitly rejected
    if (isRejected) {
      return {
        icon: XCircle,
        title: 'Verification Rejected',
        description: 'Your KYC verification has been rejected. Please review the reason below and resubmit your documents.',
        color: 'red',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        textColor: 'text-red-700',
        titleColor: 'text-red-900'
      }
    }
    
    // Check if pending or in review (only if backend explicitly says so)
    if (hasPendingKyc) {
      return {
        icon: Clock,
        title: 'Pending Review',
        description: 'Your KYC application has been submitted and is being reviewed by our admin team. You will be notified once the review is complete.',
        color: 'amber',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-200',
        textColor: 'text-amber-700',
        titleColor: 'text-amber-900'
      }
    }
    
    // Check if user has not applied for KYC yet (status is null/undefined)
    if (needsKyc) {
      return {
        icon: AlertCircle,
        title: 'KYC Needed',
        description: 'Complete your profile and upload required documents (Government ID and Proof of Address), then submit for verification.',
        color: 'red',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        textColor: 'text-red-700',
        titleColor: 'text-red-900'
      }
    }
    
    // Check if documents are uploaded but not yet submitted (edge case - should not happen with new logic)
    if (hasUploadedDocuments && !hasAppliedForKyc && !isVerified) {
      return {
        icon: AlertCircle,
        title: 'Documents Ready',
        description: 'Your documents have been uploaded. Please submit your profile for admin review to complete verification.',
        color: 'stone',
        bgColor: 'bg-stone-50',
        borderColor: 'border-stone-200',
        textColor: 'text-stone-700',
        titleColor: 'text-stone-900'
      }
    }
    
    // Default fallback: Not verified
    return {
      icon: AlertCircle,
      title: 'KYC Needed',
      description: 'Complete your profile and upload required documents (Government ID and Proof of Address), then submit for verification.',
      color: 'red',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-700',
      titleColor: 'text-red-900'
    }
  }

  const statusInfo = getStatusInfo()
  const StatusIcon = statusInfo.icon

  // Document upload component
  const DocumentUploadField = ({ 
    type, 
    label, 
    required = false, 
    description 
  }: { 
    type: keyof typeof kycDocuments
    label: string
    required?: boolean
    description?: string
  }) => {
    const hasDocument = !!kycDocuments[type]
    const isUploading = uploading && uploadingDocType === type
    const inputId = `doc-${type}`

    return (
      <div className="space-y-2">
        <label className="block text-sm font-semibold text-stone-900">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        {description && (
          <p className="text-xs text-stone-500">{description}</p>
        )}
        
        {isLocked ? (
          <div className="w-full p-4 border-2 border-dashed border-stone-200 rounded-xl bg-stone-50 text-stone-500 text-center">
            <FileText className="w-6 h-6 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Document locked after verification</p>
          </div>
        ) : (
          <div className="relative">
            <input
              type="file"
              id={inputId}
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => handleDocumentUpload(e, type)}
              className="hidden"
              disabled={isUploading}
            />
            <button
              type="button"
              onClick={() => document.getElementById(inputId)?.click()}
              disabled={isUploading}
              className={`
                w-full p-6 border-2 border-dashed rounded-xl transition-all duration-300
                ${hasDocument 
                  ? 'border-emerald-300 bg-emerald-50 hover:bg-emerald-100' 
                  : 'border-stone-300 bg-white hover:border-emerald-400 hover:bg-emerald-50'
                }
                ${isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {isUploading ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
                  <span className="text-sm font-medium text-stone-700">Uploading...</span>
                </div>
              ) : hasDocument ? (
                <div className="flex items-center justify-center gap-3">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-emerald-700">Document Uploaded</p>
                    <p className="text-xs text-emerald-600">Click to replace</p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 bg-stone-100 rounded-xl flex items-center justify-center">
                    <Upload className="w-6 h-6 text-stone-600" />
                  </div>
                  <span className="text-sm font-semibold text-stone-700">Upload {label}</span>
                  <span className="text-xs text-stone-500">PDF, JPG, or PNG (Max 10MB)</span>
                </div>
              )}
            </button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-6 border-b border-stone-200">
        <div className="flex-1">
          <h2 className="text-3xl font-display text-stone-900 mb-2">Profile & Verification</h2>
          <p className="text-stone-600">
            {isVerified
              ? 'Your profile is verified and locked. Contact support to request changes.'
              : hasPendingKyc
              ? 'Your profile is pending admin review. You will be notified once the review is complete.'
              : 'Complete your profile and submit KYC documents for verification'}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <VerificationBadge user={user} />
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="p-2 text-stone-500 hover:text-stone-700 hover:bg-stone-100 rounded-xl transition-all duration-200"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Status Card */}
      <div className={`${statusInfo.bgColor} ${statusInfo.borderColor} border-2 rounded-2xl p-6`}>
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0`}>
            <StatusIcon className={`w-6 h-6 ${statusInfo.textColor}`} />
          </div>
          <div className="flex-1">
            <h3 className={`text-lg font-display ${statusInfo.titleColor} mb-1`}>
              {statusInfo.title}
            </h3>
            <p className={`text-sm ${statusInfo.textColor} mb-3`}>
              {statusInfo.description}
            </p>
            {isRejected && (user.kyc?.rejectedReason || (user.kyc_data as any)?.rejectedReason) && (
              <div className={`mt-3 p-4 bg-white rounded-xl border ${statusInfo.borderColor}`}>
                <p className={`text-sm font-semibold ${statusInfo.titleColor} mb-1`}>Reason for Rejection:</p>
                <p className={`text-sm ${statusInfo.textColor}`}>
                  {user.kyc?.rejectedReason || (user.kyc_data as any)?.rejectedReason}
                </p>
              </div>
            )}
            {hasPendingKyc && kycStatus && (
              <p className={`text-xs ${statusInfo.textColor} mt-2`}>
                {kycStatus === 'in_review' 
                  ? 'Your documents are currently being reviewed by our admin team.'
                  : 'Our team will review your documents within 24-48 hours.'}
              </p>
            )}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Personal Information Section */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
              <UserIcon className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-xl font-display text-stone-900">Personal Information</h3>
              <p className="text-sm text-stone-600">Your basic profile details</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="firstName" className="block text-sm font-semibold text-stone-900 mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                disabled={isLocked}
                className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white text-stone-900 disabled:bg-stone-50 disabled:text-stone-500 transition-all"
                required
              />
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-semibold text-stone-900 mb-2">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                disabled={isLocked}
                className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white text-stone-900 disabled:bg-stone-50 disabled:text-stone-500 transition-all"
                required
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-stone-900 mb-2">
                <Phone className="w-4 h-4 inline mr-1" />
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                disabled={isLocked}
                className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white text-stone-900 disabled:bg-stone-50 disabled:text-stone-500 transition-all"
              />
            </div>

            <div>
              <label htmlFor="dateOfBirth" className="block text-sm font-semibold text-stone-900 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Date of Birth
              </label>
              <input
                type="date"
                id="dateOfBirth"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                disabled={isLocked}
                className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white text-stone-900 disabled:bg-stone-50 disabled:text-stone-500 transition-all"
              />
            </div>
          </div>

          <div className="mt-6">
            <label htmlFor="address" className="block text-sm font-semibold text-stone-900 mb-2">
              <MapPin className="w-4 h-4 inline mr-1" />
              Address
            </label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              rows={3}
              disabled={isLocked}
              className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white text-stone-900 disabled:bg-stone-50 disabled:text-stone-500 transition-all resize-none"
              placeholder="Enter your full address"
            />
          </div>
        </div>

        {/* KYC Documents Section - Only show if user is not verified */}
        {!isVerified && (
          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-xl font-display text-stone-900">KYC Documents</h3>
                <p className="text-sm text-stone-600">
                  {hasPendingKyc
                    ? 'Your documents are pending admin review.'
                    : 'Upload required documents for verification. All documents are securely stored and encrypted.'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <DocumentUploadField
                type="idDocument"
                label="Government ID"
                required
                description="Passport, Driver's License, or National ID"
              />
              <DocumentUploadField
                type="proofOfAddress"
                label="Proof of Address"
                required
                description="Utility bill or bank statement (not older than 3 months)"
              />
              <DocumentUploadField
                type="bankStatement"
                label="Bank Statement"
                description="Optional - helps speed up verification"
              />
              <DocumentUploadField
                type="employmentLetter"
                label="Employment Letter"
                description="Optional - proof of income"
              />
            </div>

            {/* Document Status Summary */}
            <div className="mt-6 p-4 bg-stone-50 rounded-xl border border-stone-200">
              <h4 className="font-semibold text-stone-900 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Document Status
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                  <span className="text-sm text-stone-700">Government ID:</span>
                  <span className={`text-sm font-semibold ${kycDocuments.idDocument ? 'text-emerald-600' : 'text-red-600'}`}>
                    {kycDocuments.idDocument ? '✓ Uploaded' : '✗ Required'}
                  </span>
                </div>
                <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                  <span className="text-sm text-stone-700">Proof of Address:</span>
                  <span className={`text-sm font-semibold ${kycDocuments.proofOfAddress ? 'text-emerald-600' : 'text-red-600'}`}>
                    {kycDocuments.proofOfAddress ? '✓ Uploaded' : '✗ Required'}
                  </span>
                </div>
                <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                  <span className="text-sm text-stone-700">Bank Statement:</span>
                  <span className={`text-sm font-semibold ${kycDocuments.bankStatement ? 'text-emerald-600' : 'text-stone-500'}`}>
                    {kycDocuments.bankStatement ? '✓ Uploaded' : 'Optional'}
                  </span>
                </div>
                <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                  <span className="text-sm text-stone-700">Employment Letter:</span>
                  <span className={`text-sm font-semibold ${kycDocuments.employmentLetter ? 'text-emerald-600' : 'text-stone-500'}`}>
                    {kycDocuments.employmentLetter ? '✓ Uploaded' : 'Optional'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bank Account for Payouts - Landlords only (rental payments after commission) */}
        {(user as any)?.role === 'landlord' && (
          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-5 h-5 text-sky-600" />
              </div>
              <div>
                <h3 className="text-xl font-display text-stone-900">Bank Account for Payouts</h3>
                <p className="text-sm text-stone-600">
                  Add your bank account so we can process rental payments to you after deducting our commission.
                  {isVerified && ' Verified landlords can still add or update their payout account below.'}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="bankName" className="block text-sm font-semibold text-stone-900 mb-2">
                  Bank Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="bankName"
                  name="bankName"
                  value={paymentAccount.bankName}
                  onChange={handlePaymentAccountChange}
                  className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white text-stone-900"
                  placeholder="e.g. GTBank, Access Bank"
                />
              </div>
              <div>
                <label htmlFor="accountName" className="block text-sm font-semibold text-stone-900 mb-2">
                  Account Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="accountName"
                  name="accountName"
                  value={paymentAccount.accountName}
                  onChange={handlePaymentAccountChange}
                  className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white text-stone-900"
                  placeholder="Name as it appears on account"
                />
              </div>
              <div>
                <label htmlFor="accountNumber" className="block text-sm font-semibold text-stone-900 mb-2">
                  <CreditCard className="w-4 h-4 inline mr-1" />
                  Account Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="accountNumber"
                  name="accountNumber"
                  value={paymentAccount.accountNumber}
                  onChange={handlePaymentAccountChange}
                  className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white text-stone-900"
                  placeholder="10-digit account number"
                  inputMode="numeric"
                  maxLength={20}
                />
              </div>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleSaveBankAccount}
                disabled={savingBankAccount || !paymentAccount.bankName?.trim() || !paymentAccount.accountName?.trim() || !paymentAccount.accountNumber?.trim()}
                className="px-6 py-3 bg-sky-600 text-white rounded-xl hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-all"
              >
                {savingBankAccount ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </span>
                ) : (
                  'Save Bank Account'
                )}
              </button>
              {paymentAccount.accountNumber && (
                <span className="text-sm text-stone-500">
                  Last digits: ••••{paymentAccount.accountNumber.slice(-4)}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Message Display */}
        {message && (
          <div className={`p-4 rounded-xl border-2 ${
            message.includes('error') || message.includes('Failed') 
              ? 'bg-red-50 text-red-700 border-red-200' 
              : 'bg-emerald-50 text-emerald-700 border-emerald-200'
          }`}>
            <div className="flex items-center gap-2">
              {message.includes('error') || message.includes('Failed') ? (
                <XCircle className="w-5 h-5 flex-shrink-0" />
              ) : (
                <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
              )}
              <span className="font-medium">{message}</span>
            </div>
          </div>
        )}

        {/* Form Actions */}
        {!isLocked && (
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-6 border-t border-stone-200">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-3 border-2 border-stone-300 text-stone-700 rounded-xl hover:bg-stone-50 font-semibold transition-all duration-300"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={loading || !kycDocuments.idDocument || !kycDocuments.proofOfAddress}
              className="px-8 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/25"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Submitting...
                </span>
              ) : (
                'Submit for Verification'
              )}
            </button>
          </div>
        )}
      </form>

      {/* Verification Process Info */}
      {!isVerified && (
        <div className="bg-gradient-to-br from-emerald-50 to-stone-50 rounded-2xl border border-emerald-200 p-6">
          <h4 className="font-display text-lg text-emerald-900 mb-4">Verification Process</h4>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {[
              { step: 1, title: 'Upload Documents', desc: 'Upload required KYC documents' },
              { step: 2, title: 'Submit Profile', desc: 'Submit your profile for review' },
              { step: 3, title: 'Admin Review', desc: 'Admin reviews within 24-48 hours' },
              { step: 4, title: 'Notification', desc: 'Receive email when verified' },
              { step: 5, title: 'Access Features', desc: 'Unlock full platform features' }
            ].map((item, index) => (
              <div key={item.step} className="text-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm mb-2 mx-auto ${
                  index === 0 && !hasPendingKyc ? 'bg-emerald-600 text-white' :
                  hasPendingKyc && index < 2 ? 'bg-emerald-600 text-white' :
                  isVerified ? 'bg-emerald-600 text-white' :
                  'bg-stone-200 text-stone-600'
                }`}>
                  {item.step}
                </div>
                <p className="text-xs font-semibold text-stone-900 mb-1">{item.title}</p>
                <p className="text-xs text-stone-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
